import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { mono } from '../ui';
import { PreflightCheck, PreflightResult } from './types';

// Shared credential pre-flight UI, used by the scaffolder creation field and the
// Day-2 Manage tab so both render identically: a summary verdict plus two lanes
// (GitHub App / LLM Hub) whose steps reveal in sequence as the check settles.

const SPIN_KEYFRAMES = `@keyframes bui-preflight-spin { to { transform: rotate(360deg); } }`;
const REVEAL_STEP_MS = 300;

const CheckMark = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth={3.2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const CrossMark = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth={3.2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const BadgeCheck = () => (
  <svg
    viewBox="0 0 24 24"
    width="30"
    height="30"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);
const BadgeAlert = () => (
  <svg
    viewBox="0 0 24 24"
    width="30"
    height="30"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);
const GithubGlyph = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.52 2.34 1.08 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02a9.5 9.5 0 0 1 5 0c1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2Z" />
  </svg>
);
const ShieldGlyph = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    aria-hidden
  >
    <path d="M12 2 4 6v6c0 5 3.4 7.7 8 10 4.6-2.3 8-5 8-10V6l-8-4Z" />
  </svg>
);

type PreflightLane = { key: string; name: string; checks: PreflightCheck[] };

const lanesOf = (
  checks: PreflightCheck[],
  laneName?: string,
): PreflightLane[] => {
  if (laneName)
    return checks.length ? [{ key: 'single', name: laneName, checks }] : [];
  const github = checks.filter(c => c.id.startsWith('github'));
  const llmHub = checks.filter(c => !c.id.startsWith('github'));
  const lanes: PreflightLane[] = [];
  if (github.length)
    lanes.push({ key: 'gh', name: 'GitHub App', checks: github });
  if (llmHub.length)
    lanes.push({ key: 'llm', name: 'LLM Hub', checks: llmHub });
  return lanes;
};

const sleep = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

const hint: CSSProperties = { fontSize: 12, color: 'var(--bui-fg-secondary)' };

const nodeMark = (settled: boolean, status: PreflightCheck['status']) => {
  if (!settled) {
    return (
      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          border: '2px solid var(--bui-fg-link)',
          borderRightColor: 'transparent',
          display: 'inline-block',
          animation: 'bui-preflight-spin .7s linear infinite',
        }}
      />
    );
  }
  if (status === 'ok') return <CheckMark />;
  if (status === 'ko') return <CrossMark />;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>!</span>
  );
};

const VerifyStep = ({
  check,
  settled,
  last,
}: {
  check: PreflightCheck;
  settled: boolean;
  last: boolean;
}) => {
  const pass = settled && check.status === 'ok';
  const fail = settled && check.status === 'ko';
  const warn = settled && check.status === 'warn';
  const nodeColor = pass
    ? 'var(--bui-fg-success)'
    : fail
    ? 'var(--bui-fg-danger)'
    : warn
    ? 'var(--bui-fg-warning)'
    : 'var(--bui-fg-secondary)';
  const nodeBg = pass
    ? 'var(--bui-bg-success)'
    : fail
    ? 'var(--bui-bg-danger)'
    : warn
    ? 'var(--bui-bg-warning)'
    : 'var(--bui-bg-neutral-1)';
  return (
    <div
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '22px 1fr',
        gap: 10,
        paddingBottom: last ? 0 : 12,
      }}
    >
      {!last && (
        <span
          style={{
            position: 'absolute',
            left: 10,
            top: 22,
            bottom: 0,
            width: 2,
            background: settled
              ? fail
                ? 'var(--bui-border-danger)'
                : 'var(--bui-border-success)'
              : 'var(--bui-border-1)',
          }}
        />
      )}
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          zIndex: 1,
          border: settled
            ? '1px solid transparent'
            : '2px dashed var(--bui-border-2)',
          background: nodeBg,
          color: nodeColor,
        }}
      >
        {nodeMark(settled, check.status)}
      </span>
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 550,
            lineHeight: 1.35,
            color: fail
              ? 'var(--bui-fg-danger)'
              : settled
              ? 'var(--bui-fg-primary)'
              : 'var(--bui-fg-secondary)',
          }}
        >
          {check.label}
        </div>
        {check.detail && settled && (
          <div
            style={{
              ...mono,
              fontSize: 11,
              color: 'var(--bui-fg-secondary)',
              marginTop: 2,
              wordBreak: 'break-word',
            }}
          >
            {check.detail}
          </div>
        )}
      </div>
    </div>
  );
};

const VerifyLane = ({ lane, tick }: { lane: PreflightLane; tick: number }) => (
  <div
    style={{
      border: '1px solid var(--bui-border-1)',
      borderRadius: 10,
      padding: '13px 15px',
      background: 'var(--bui-bg-neutral-1)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
      }}
    >
      <span
        style={{ color: 'var(--bui-fg-secondary)', display: 'inline-flex' }}
      >
        {lane.key === 'gh' ? <GithubGlyph /> : <ShieldGlyph />}
      </span>
      <span style={{ fontWeight: 650, fontSize: 13.5 }}>{lane.name}</span>
      <span
        style={{
          ...mono,
          marginLeft: 'auto',
          fontSize: 11,
          color: 'var(--bui-fg-secondary)',
        }}
      >
        {lane.checks.length} checks
      </span>
    </div>
    <div>
      {lane.checks.map((check, i) => (
        <VerifyStep
          key={check.id}
          check={check}
          settled={i < tick}
          last={i === lane.checks.length - 1}
        />
      ))}
    </div>
  </div>
);

const VerifyVerdict = ({ result }: { result: PreflightResult }) => {
  const fail = result.checks.filter(c => c.status === 'ko').length;
  const warn = result.checks.filter(c => c.status === 'warn').length;
  const ok = fail === 0;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        borderRadius: 10,
        background: ok ? 'var(--bui-bg-success)' : 'var(--bui-bg-danger)',
        border: `1px solid ${
          ok ? 'var(--bui-border-success)' : 'var(--bui-border-danger)'
        }`,
      }}
    >
      <span
        style={{
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          color: ok ? 'var(--bui-fg-success)' : 'var(--bui-fg-danger)',
        }}
      >
        {ok ? <BadgeCheck /> : <BadgeAlert />}
      </span>
      <div>
        <div
          style={{
            fontWeight: 660,
            fontSize: 14,
            color: ok ? 'var(--bui-fg-success)' : 'var(--bui-fg-danger)',
          }}
        >
          {ok
            ? 'All checks passed'
            : `${fail} ${fail === 1 ? 'issue' : 'issues'} found${
                warn ? `, ${warn} warning${warn === 1 ? '' : 's'}` : ''
              }`}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: 'var(--bui-fg-secondary)',
            marginTop: 2,
          }}
        >
          {ok
            ? 'These credentials will authenticate at runtime.'
            : 'Fix the failing checks before you rely on this squad.'}
        </div>
      </div>
    </div>
  );
};

const button = (disabled: boolean): CSSProperties => ({
  font: 'inherit',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: disabled ? 'default' : 'pointer',
  borderRadius: 6,
  padding: '7px 14px',
  border: '1px solid var(--bui-border-2)',
  background: 'var(--bui-bg-neutral-1)',
  color: 'var(--bui-fg-primary)',
  opacity: disabled ? 0.6 : 1,
  whiteSpace: 'nowrap',
});

const DEFAULT_DESCRIPTION =
  'Replays the GitHub App and LLM Hub authentication chains PANDA uses at ' +
  'runtime, using the stored credentials. Read-only: nothing is written and no ' +
  'secret value is shown.';

export const CredentialPreflight = ({
  onVerify,
  onResult,
  onReset,
  resetSignal,
  title = 'Verify credentials',
  description = DEFAULT_DESCRIPTION,
  laneName,
}: {
  onVerify: () => Promise<PreflightResult>;
  onResult?: (result: PreflightResult) => void;
  onReset?: () => void;
  resetSignal?: string;
  title?: string;
  description?: string;
  laneName?: string;
}) => {
  const [phase, setPhase] = useState<'idle' | 'loading' | 'revealing' | 'done'>(
    'idle',
  );
  const [result, setResult] = useState<PreflightResult>();
  const [tick, setTick] = useState(0);
  const [error, setError] = useState<string>();
  const busy = phase === 'loading' || phase === 'revealing';

  const reset = useCallback(() => {
    setPhase('idle');
    setResult(undefined);
    setTick(0);
    setError(undefined);
  }, []);

  const run = useCallback(async () => {
    setPhase('loading');
    setError(undefined);
    setResult(undefined);
    setTick(0);
    let outcome: PreflightResult;
    try {
      outcome = await onVerify();
    } catch (e: any) {
      setError(e?.message ?? 'The verification could not be completed.');
      setPhase('idle');
      return;
    }
    setResult(outcome);
    onResult?.(outcome);
    const maxLen = lanesOf(outcome.checks, laneName).reduce(
      (m, lane) => Math.max(m, lane.checks.length),
      0,
    );
    const reduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || maxLen === 0) {
      setTick(maxLen);
      setPhase('done');
      return;
    }
    // Reveal each step in sequence so the check reads as it happens; both lanes
    // advance on the same tick.
    setPhase('revealing');
    for (let t = 1; t <= maxLen; t++) {
      await sleep(REVEAL_STEP_MS);
      setTick(t);
    }
    setPhase('done');
  }, [onVerify, onResult]);

  // Invalidate a prior run when the inputs change (the caller passes a new
  // resetSignal), so a stale verdict never lingers.
  const prevSignal = useRef(resetSignal);
  useEffect(() => {
    if (prevSignal.current === resetSignal) return;
    prevSignal.current = resetSignal;
    if (phase !== 'idle') {
      reset();
      onReset?.();
    }
  }, [resetSignal, phase, reset, onReset]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <style>{SPIN_KEYFRAMES}</style>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontWeight: 660, fontSize: 15 }}>{title}</div>
          <div style={{ ...hint, marginTop: 4, maxWidth: '56ch' }}>
            {description}
          </div>
        </div>
        <button
          type="button"
          style={button(busy)}
          disabled={busy}
          onClick={run}
        >
          {phase === 'loading'
            ? 'Verifying…'
            : phase === 'revealing'
            ? 'Checking…'
            : result
            ? 'Run again'
            : 'Run pre-flight'}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{ fontSize: 12.5, color: 'var(--bui-fg-danger)' }}
        >
          {error}
        </div>
      )}

      {phase === 'done' && result && <VerifyVerdict result={result} />}

      {result && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 14,
          }}
        >
          {lanesOf(result.checks, laneName).map(lane => (
            <VerifyLane key={lane.key} lane={lane} tick={tick} />
          ))}
        </div>
      )}
    </div>
  );
};
